/**
 * VoiceTable — tabella delle righe di una singola voce bolletta (es. A, B, C…).
 *
 * Replica del componente mobile (VoiceTable.jsx):
 *   - header: pallino colorato + codice voce + label + totale voce
 *   - tabella sotto: "Dettaglio" / "Imponibile" (header) + N righe
 *   - imponibili negativi in colore success (verde)
 */

import { Card } from '@/components/ui/card'
import type { VoiceItem } from '@/lib/graphql/types'
import { cn } from '@/lib/utils'
import { COLORS_PIE, EURO, VOCI_BOLLETTA } from '../constants'

type Props = {
  voice: string
  items: VoiceItem[]
}

export function VoiceTable({ voice, items }: Props) {
  const color = COLORS_PIE[voice] ?? 'hsl(var(--primary))'
  const total = items.reduce((sum, it) => sum + (Number(it.imponibile) || 0), 0)
  const label = VOCI_BOLLETTA[voice] ?? voice

  return (
    <Card className="p-5">
      <header className="flex items-center gap-3">
        <span
          aria-hidden
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />
        <h3 className="flex-1 text-sm font-bold text-foreground">
          {voice}: {label}
        </h3>
        <span className="text-sm font-bold tabular-nums text-foreground">
          {total.toFixed(2).replace('.', ',')} {EURO}
        </span>
      </header>

      {items.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center border-b border-border pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <span className="flex-[3]">Dettaglio</span>
            <span className="flex-1 text-right">Imponibile</span>
          </div>
          <ul>
            {items.map((it, idx) => (
              <li
                key={`${voice}-${idx}`}
                className={cn(
                  'flex items-center py-2.5 text-sm',
                  idx < items.length - 1 && 'border-b border-border',
                )}
              >
                <span className="flex-[3] text-foreground">{it.name}</span>
                <span
                  className={cn(
                    'flex-1 text-right font-medium tabular-nums',
                    it.imponibile < 0 ? 'text-success' : 'text-foreground',
                  )}
                >
                  {Number(it.imponibile).toFixed(2).replace('.', ',')} {EURO}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  )
}
